import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import OutlinedInput from '@mui/material/OutlinedInput';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { changeOperatorPassword } from 'api/UserApi';
import usePartnerAuth from 'hooks/usePartnerAuth';

interface Props {
    open: boolean;
    onClose: () => void;
}

const ChangePasswordModal = ({ open, onClose }: Props) => {
    const { user } = usePartnerAuth();
    const theme = useTheme();
    const [form, setForm] = useState({ password: '', confirmPassword: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setForm({ password: '', confirmPassword: '' });
            setError('');
            setSuccess('');
            setLoading(false);
        }
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.password || !form.confirmPassword) {
            setError('All fields are required.');
            return;
        }
        if (form.password.length < 6) {
            setError('New password must be at least 6 characters.');
            return;
        }
        if (form.password !== form.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setError('');
        setLoading(true);
        const result = await changeOperatorPassword(String(user?.userId ?? ''), form.password, form.confirmPassword);
        setLoading(false);
        if (!result.status) {
            setError(result.error ?? 'Failed to change password. Please try again.');
            return;
        }
        setSuccess('Password changed successfully.');
        setForm({ password: '', confirmPassword: '' });
    };

    const fieldSx = {
        background: '#171B26',
        borderRadius: '10px',
        color: '#fff',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.secondary.main },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.secondary.main },
        '& input::placeholder': { color: '#6B7280', opacity: 1 }
    };

    const labelSx = { color: '#fff', fontWeight: 700, fontSize: '0.9rem', mb: 0.75, display: 'block' };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: '#12151E', borderRadius: 3 } }}>
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.25rem' }}>Change Password</Typography>
                        <IconButton size="small" onClick={onClose} sx={{ color: '#9CA3AF', background: 'rgba(255,255,255,0.06)' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2.5 }} />

                    <Stack spacing={2.5}>
                        {success ? (
                            <>
                                <Alert severity="success">{success}</Alert>
                                <Button
                                    onClick={onClose}
                                    fullWidth
                                    size="large"
                                    variant="outlined"
                                    sx={{
                                        borderColor: theme.palette.secondary.main,
                                        color: theme.palette.secondary.main,
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        py: 1.5,
                                        borderRadius: '10px',
                                        '&:hover': { borderColor: theme.palette.secondary.main, backgroundColor: 'rgba(255,255,255,0.05)' }
                                    }}
                                >
                                    Close
                                </Button>
                            </>
                        ) : (
                            <>
                                {error && <Alert severity="error">{error}</Alert>}
                                <Box>
                                    <Typography component="label" sx={labelSx}>
                                        New Password
                                    </Typography>
                                    <OutlinedInput
                                        type="password"
                                        fullWidth
                                        placeholder="At least 6 characters"
                                        value={form.password}
                                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                        disabled={loading}
                                        sx={fieldSx}
                                    />
                                </Box>
                                <Box>
                                    <Typography component="label" sx={labelSx}>
                                        Confirm New Password
                                    </Typography>
                                    <OutlinedInput
                                        type="password"
                                        fullWidth
                                        placeholder="Re-enter new password"
                                        value={form.confirmPassword}
                                        onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                                        disabled={loading}
                                        sx={fieldSx}
                                    />
                                </Box>

                                <Stack direction="row" spacing={1.5}>
                                    <Button
                                        onClick={onClose}
                                        fullWidth
                                        size="large"
                                        variant="outlined"
                                        disabled={loading}
                                        sx={{
                                            borderColor: 'rgba(255,255,255,0.2)',
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '16px',
                                            py: 1.5,
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            '&:hover': { borderColor: 'rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.05)' }
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <AnimateButton>
                                        <Button
                                            type="submit"
                                            fullWidth
                                            size="large"
                                            variant="contained"
                                            disabled={loading}
                                            sx={{
                                                backgroundColor: theme.palette.secondary.main,
                                                color: '#111',
                                                boxShadow: 'none',
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                py: 1.5,
                                                borderRadius: '10px',
                                                textTransform: 'none',
                                                '&:hover': { backgroundColor: theme.palette.secondary.dark ?? theme.palette.secondary.main },
                                                '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.3)' }
                                            }}
                                        >
                                            {loading ? <CircularProgress size={22} sx={{ color: '#111' }} /> : 'Update'}
                                        </Button>
                                    </AnimateButton>
                                </Stack>
                            </>
                        )}
                    </Stack>
                </DialogContent>
            </form>
        </Dialog>
    );
};

export default ChangePasswordModal;
